import { attr, Component } from "../Component";
import { element } from "../CustomElement";
import {getLocaleFromUrl, i18n, i18nForLocale} from "../../i18n/I18n";
import { icons } from "../../icons/Icons";
import { isCrawler } from "../../utils/crawler";
import cx from "classnames";

import {Dialog, DialogHeader} from "../flipp-dialog/Dialog";
import { Link } from "../flipp-link/Link";
import { OldBrowserContainer } from "../flipp-old-browser-container/OldBrowserContainer";
import { WebGLContainer } from "../flipp-webgl-container/WebGLContainer";
import { ApplicationTopBar } from "../flipp-application-topbar/ApplicationTopBar";
import { CircularDestinationHeader } from "../flipp-circular-destination-header/CircularDestinationHeader";
import { AppRating } from "../flipp-app-rating/AppRating";
import { DownloadAppBanner } from "../flipp-download-app-banner/DownloadAppBanner";

import { Category, Clipping, ClippingType } from "../../definitions/CommonDefinitions";
import { ClippedCoupon } from "../../definitions/CouponDefinitions";
import { inject } from "../../services/di";
import { Country, LocationService, PostalCode } from "../../services/LocationService";
import { BlacklistService } from "../../services/BlacklistService";
import { FlyerService } from "../../services/FlyerService";
import { AnalyticsService } from "../../services/AnalyticsService";
import { ClippingService } from "../../services/ClippingService";
import { AuthenticationService } from "../../services/AuthenticationService";
import { AuthStatus } from "../../services/definitions/IAuthenticationService";
import { CouponService } from "../../services/CouponService";
import { LinkService } from "../../services/LinkService";
import { ConfigService, DistributionMode, Experience, RuntimeConfig } from "../../services/ConfigService";
import { UserAgentService } from "../../services/UserAgentService";
import { StorageService } from "../../services/StorageService";
import { AccountsService } from "../../services/AccountsService";
import { CrossBrowseAction } from "../../services/definitions/IAnalyticsService";
import { SeoService } from "../../services/SeoService";
import BreadcrumbsService, { IBreadcrumb } from "../../services/BreadcrumbsService";
import { MobileBanner } from "../flipp-mobile-banner/MobileBanner";
import { FlippCitiesFooterLinks } from "../flipp-cities-footer-links/FlippCitiesFooterLinks";
import MetadataService from "../../services/MetadataService";
import { SeoDataService } from "../../services/SeoDataService";
import { DataFormService } from "../../services/DataFormService";
import { BlackFridayService } from "../../services/BlackFridayService";
import { BannerService } from "../../services/BannerService";
import {CategoryService} from "../../services/CategoryService";
import {forEach} from "lodash-es";
import {slugify} from "../../utils/slugify";
import {renderDeleteAccountModal} from "../../utils/modal";

export type PageContext = "shoppingList" | "default";

interface PageProperties extends JSX.HTMLProps {
	show: "flyers" | "coupons";
	withSidebar?: string;
	search?: string;
	context?: PageContext;
	merchant?: string;
}

type PageModel = [Category[], IBreadcrumb[]];
@element("flipp-page")
export class Page extends Component<PageProperties, PageModel> {
	refs: {
		locationDialog: Dialog,
		anchor: HTMLButtonElement,
		postalCodeInput: HTMLInputElement,
		publisherName: HTMLInputElement,
		content: HTMLElement,
		sidebarDialog: Dialog,
		emailConfirm: Dialog;
		welcome: Dialog;
		sidebarLinks: HTMLElement;
		languageButton: HTMLElement;
	};

	@inject location: LocationService;
	@inject storage: StorageService;
	@inject couponService: CouponService;
	@inject flyerService: FlyerService;
	@inject userAgent: UserAgentService;
	@inject accounts: AccountsService;
	@inject auth: AuthenticationService;
	@inject config: ConfigService;
	@inject analytics: AnalyticsService;
	@inject blacklist: BlacklistService;
	@inject seo: SeoService;
	@inject breadcrumbsService: BreadcrumbsService;
	@inject linkService: LinkService;
	@inject metadataService: MetadataService;
	@inject seoData: SeoDataService;
	@inject dataFormService: DataFormService;
	@inject bf: BlackFridayService;
	@inject bannerService: BannerService;
	@inject categoryService: CategoryService;

	@attr show: string;
	@attr search: string;
	@attr context: PageContext;
	@attr withSidebar: string = "true";
	@attr showCouponsCategories: string = "true";
	@attr merchant: string;

	isModalClosed = true;
	appBannerClosed = false;

	onAttach() {
		window.addEventListener("resize", this._handleResize);
		window.addEventListener("flipp-auth", this._handleAuth);
		// need to allow for space for other child elements, so applying class on the top level
		this.className = this.config.CDP_SIMPLE_HEADER ? "circular-destination-mode" : "";
		window.addEventListener("unsubscribe-from-redirect", this.dataFormService.unsubscribeFromRedirect);
		window.addEventListener("mobile-banner-closed", this.resizeButton.bind(this));
	}

	onDetach() {
		window.removeEventListener("resize", this._handleResize);
		window.removeEventListener("flipp-auth", this._handleAuth);
		window.removeEventListener("unsubscribe-from-redirect", this.dataFormService.unsubscribeFromRedirect);
		window.removeEventListener("mobile-banner-closed", this.resizeButton);
	}

	get scrollTop() {
		return this.refs.content.scrollTop;
	}

	set scrollTop(top: number) {
		this.refs.content.scrollTop = top;
	}

	prepare() {
		return Promise.all([
			isCrawler() ? null : this.show === "coupons" ?
				this.couponService.getCouponCategories() :
				this.flyerService.getCategories(),
			this.getBreadcrumbs(),
		]);
	}

	renderLoading() {
		return this._doRender(true, [], []);
	}

	closeWelcomeDialog() {
		this.refs.welcome.close();
	}

	render([categories, breadcrumbs]: PageModel) {
		return this._doRender(false, categories, breadcrumbs);
	}

	checkMobileBanner() {
		const languageButton = this.refs.languageButton;
		const sidebarLinks = this.refs.sidebarLinks;
		if (!this.appBannerClosed && languageButton) {
			languageButton.classList.add("higher-button");
		} else if (!this.appBannerClosed && sidebarLinks) {
			sidebarLinks.classList.add("higher-links");
		}
	}

	resizeButton() {
		this.invalidate(true).then(() => {
			const bA = document.querySelector(".button-area");
			if (bA) {
				bA.classList.remove("higher-button");
			}
			const sL = document.querySelector(".sidebar-links");
			if (sL) {
				sL.classList.remove("higher-links");
			}
		});
	}

	getBreadcrumbs(): Promise<IBreadcrumb[]> {
		return isCrawler() ? this.breadcrumbsService.getBreadcrumbs() : Promise.resolve([]);
	}

	renderBreadcrumbs(breadcrumbs: IBreadcrumb[]) {
		return <ol class="breadcrumbs">
			{breadcrumbs.map((breadcrumb) => (
				<li>
					<a href={breadcrumb.path}>
						{breadcrumb.label}
					</a>
				</li>
			))}
		</ol>;
	}

	getMerchantLink() {
		return isCrawler() ? <a href={this.linkService.getMerchantHref(this.merchant)}>{this.merchant} flyers coupons and deals</a> : null;
	}

	getTopMerchants() {
		const merchants = getLocaleFromUrl() === "en-us" ? this.seoData.getTopUsMerchants() : this.seoData.getTopCanadaMerchants();
		return merchants.slice(20);
	}

	getLocaleFromUrl() {
		const locale = window.location.pathname.split("/")[1];
		if (this.seo.isLocale(locale)) {
			return locale;
		}

		return this.metadataService.getLocale;
	}

	renderMerchantsLinks() {
		if (!isCrawler()) {
			return null;
		}

		const links = this.getTopMerchants().map((merchant: string) => {
			return <li><a href={this.linkService.getMerchantHref(merchant, this.getLocaleFromUrl())}>{merchant}</a></li>;
		});
		return <div><ul>{links}</ul></div>;
	}

	getFooter() {
		return (
			<footer>
				{this.renderMerchantsLinks()}
				<FlippCitiesFooterLinks />
			</footer>
		);
	}

	checkAppBanner = (whenWasHidden: any) => {
		if (whenWasHidden) {
			this.appBannerClosed = true;
		}
	}

	private handleAppLinkClick_ = (e: Event) => {
		if (this.userAgent.android || this.userAgent.iOS) {
			this.analytics.trackMobileInstall(this.userAgent.android ? "android" : "ios");
		}
	}

	private renderCategories = (mobile: boolean, categories: Category[]) => {
		return categories && categories.map((category) =>
				<Link to={category}>
					<span flex-grow>{i18n(category.name)}</span>
					<span class="pill">{category.count}</span>
				</Link>
			);
	}

	private renderFlyersCategories = (mobile: boolean, categories: Category[]) => {
		const appLink = this.userAgent.getAppLinkHref("Application Top Bar");
		const helpLink = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ?
			"https://flipp.freshdesk.com/support/home" :
			i18n("help_link");
		if (mobile) {
			const feedbackClass = this.location.locale === "fr-ca" ? "french" : "";
			return (
				<div>
					{this.renderCategories(mobile, categories.slice(0, 6))}
					<hr/>
					<div ref="sidebarLinks" class="sidebar-links">
						<a target="_blank" href="https://corp.flipp.com">{i18n("company")}</a>
						<a target="_blank" href={i18n("about_us_link")}>{i18n("about_us")}</a>
						<a target="_blank" href="https://corp.flipp.com/careers">{i18n("careers")}</a>
						{!this.bannerService.isSmartNews() && (
							<a
								target="_blank"
								href={appLink}
								onClick={(e: Event) => {
									this.handleAppLinkClick_(e);
								}}
							>
								{i18n("mobile_app")}
							</a>
						)}
						<a target="_blank" href={helpLink}>{i18n("help")}</a>
						<hr/>
						<a target="_blank" href="https://corp.flipp.com/flipp/terms_of_use">{i18n("terms_of_use")}</a>
						<a target="_blank" href="https://corp.flipp.com/flipp/privacy">{i18n("privacy_policy")}</a>
						<button class="side-bar-btn"
								onClick={this.dataFormService.handleFormClicked}>{i18n("my_data")}</button>
						<button class="side-bar-btn"
							onClick={this.openDeleteAccountPopup}>
							{i18n("delete_account_link")}</button>
						<a target="_blank" href="https://corp.flipp.com/patents">{i18n("patents")}</a>
						<a target="_blank" href="https://corp.flipp.com/privacy#INFO55">{i18n("ad_choices")}</a>
						<a href="https://www.flippappinions.com/c/a/6kiwEIx1Ce287lz8RyHYr8" target="blank">
							<button class="side-bar-btn-feedback">
								<img src="https://f.wishabi.net/creative/Distribution/images/icons/feedback.svg" width="44px" height="26px" alt=""/>
								<span class={feedbackClass}>{i18n("give_feedback")}</span></button>
						</a>
						<hr/>
					</div>
				</div>

			);
		} else {
			return this.renderCategories(mobile, categories);
		}
	}

	private _renderNavigation(loading: boolean, categories: Category[]) {
		const changeLocation = i18n("change_location");
		const flyersForPostalCode = i18n("flyers_for_postal_code", {
			postalCode: this.location.postalCode
		});
		const canada = this.location.country === Country.CANADA && this.location.locale !== "en-us";
		const french = this.location.acceptsLocale("fr");
		const analyticParams = this.analytics.analyticsParameters;

		return	(
			<Dialog ref="sidebarDialog"
							class="sidebar-dialog"
							anchor-point="left top">
				<nav class={cx({"bf": this.bf.isOnHost()})} flex-vertical flex-noshrink>
					{this.getPoweredByText()}
					<div>
						<Link to="listing" class="logo" title="Flipp">
							{icons.flippLogo}
						</Link>
					</div>
					{this.userAgent.mobile && !this.bannerService.isSmartNews()
						?
						<div class="get-app-block">
							<div class="get-app-stars">
								<AppRating />
							</div>
							<div class="get-app-button">
								<a href="https://click.flipp.com/h8pE/cd8c8676">
									<button>{i18n("get_app")}</button>
								</a>
							</div>
						</div>
						: null
					}

					<div class={cx("sidebar", {"sidebar--bf": this.bf.isOnHost()})} flex-grow flex-vertical>
						{isCrawler() ? null : (
							<button class="location" ref="anchor"
									anchor-point="left bottom"
									onClick={this._handleLocationButtonClick_}>
								{
									this.bf.isOnHost() ? <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
									<path d="M9 15.75C8.87909 15.7498 8.76002 15.7204 8.65293 15.6642C8.54585 15.6081 8.45393 15.5269 8.385 15.4275L4.3875 9.61501L4.3425 9.54751C3.96047 8.84289 3.76038 8.05404 3.76038 7.25251C3.76038 6.45098 3.96047 5.66214 4.3425 4.95751C4.74423 4.20134 5.32858 3.55756 6.04244 3.08471C6.7563 2.61186 7.57703 2.32493 8.43 2.25001C8.80941 2.22001 9.1906 2.22001 9.57 2.25001C10.423 2.32154 11.2448 2.60448 11.9611 3.07326C12.6774 3.54204 13.2655 4.1819 13.6725 4.93501C14.0545 5.63964 14.2546 6.42848 14.2546 7.23001C14.2546 8.03154 14.0545 8.82039 13.6725 9.52501C13.6593 9.54864 13.6442 9.57121 13.6275 9.59251L9.63 15.405C9.56224 15.5106 9.46906 15.5976 9.35899 15.6578C9.24893 15.7181 9.12549 15.7498 9 15.75ZM5.64 8.79751L9 13.68L12.3525 8.79751C12.6144 8.31656 12.7516 7.77765 12.7516 7.23001C12.7516 6.68237 12.6144 6.14346 12.3525 5.66251C12.0693 5.12169 11.6526 4.66216 11.1421 4.32747C10.6315 3.99277 10.0439 3.79401 9.435 3.75001C9.14578 3.72 8.85423 3.72 8.565 3.75001C7.95666 3.79609 7.36994 3.9957 6.85971 4.33017C6.34948 4.66463 5.93241 5.12304 5.6475 5.66251C5.38641 6.14347 5.24902 6.68181 5.24771 7.22906C5.2464 7.77632 5.38121 8.31531 5.64 8.79751Z" fill="white"/>
									<path d="M9 9.375C10.0355 9.375 10.875 8.53553 10.875 7.5C10.875 6.46447 10.0355 5.625 9 5.625C7.96447 5.625 7.125 6.46447 7.125 7.5C7.125 8.53553 7.96447 9.375 9 9.375Z" fill="white"/>
								</svg> : <img class="icon" src="https://f.wishabi.net/creative/Distribution/images/icons/location-01-default5.svg" alt=""/>
								}
								<div>
									{flyersForPostalCode}<br/>
									<span>({changeLocation})</span>
								</div>
							</button>
						)}

						<Dialog anchor-point="left top"
								ref="locationDialog"
								class="location-dialog popover">
							<div class="arrow"></div>
							<form class="postal-code"
									method="get"
									action={LinkService.getInstance().linkTo(this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ?
										"distribution" : "redirect")}
									onSubmit={this._handlePostalCodeSubmit}>
								<input type="hidden" name="page" value="listing" />
								<input type="hidden" name="locale" value={this.location.locale} />
								<input type="hidden" name="old_fsa" value={this.config.RUNTIME === RuntimeConfig.DEFAULT ?
									this.location.postalCode : null} />
								<input type="hidden" name="location_source" value="1" /> {/* 1 is 'USER_SUPPLIED' */}
								<input type="hidden" name="blacklist_merchant_ids" value={this.blacklist.getBlacklist().join(",")} />
								{this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ?
									Object.getOwnPropertyNames(analyticParams).map((prop) =>
										<input type="hidden" name={prop} value={analyticParams[prop]} />
									)
									:
									null
								}
								{ this.config.CDP_SIMPLE_HEADER ?
									<div>
										<input ref="publisherName" type="hidden" name="publisher_name" value={this.config.CDP_PUBLISHER_NAME} />
										<input type="hidden" name="website_url" value={this.config.CDP_BACK_LINK} />
									</div>
									:
									null
								}
								{
									(!isCrawler()) ?
										<div>
											<p>{i18n("enter_your_postal_code")}</p>
											<input type="text" name="postal_code"
												placeholder={i18n("postal_code")}
												value={this.location.postalCode}
												maxLength={7}
												ref="postalCodeInput"/>
											<button type="submit" class="find-flyers">{i18n("find_flyers")}</button>
										</div> : null
								}
								</form>
						</Dialog>
						{this.userAgent.mobile ?
							<Link to="shopping_list">
								<div class="myCustomElement">
									{
										this.bf.isOnHost() ?
											<svg width="20" height="20" viewBox="0 0 20 20" fill="#43C4F4">
												<path d="M6.66666 4.24999C6.44565 4.24999 6.23369 4.16219 6.07741 4.00591C5.92113 3.84963 5.83333 3.63767 5.83333 3.41666V2.49999C5.83333 2.27898 5.92113 2.06701 6.07741 1.91073C6.23369 1.75445 6.44565 1.66666 6.66666 1.66666C6.88768 1.66666 7.09964 1.75445 7.25592 1.91073C7.4122 2.06701 7.5 2.27898 7.5 2.49999V3.41666C7.5 3.63767 7.4122 3.84963 7.25592 4.00591C7.09964 4.16219 6.88768 4.24999 6.66666 4.24999Z" fill="#43C4F4"/>
												<path d="M10 4.24999C9.77899 4.24999 9.56703 4.16219 9.41075 4.00591C9.25447 3.84963 9.16667 3.63767 9.16667 3.41666V2.49999C9.16667 2.27898 9.25447 2.06701 9.41075 1.91073C9.56703 1.75445 9.77899 1.66666 10 1.66666C10.221 1.66666 10.433 1.75445 10.5893 1.91073C10.7455 2.06701 10.8333 2.27898 10.8333 2.49999V3.41666C10.8333 3.63767 10.7455 3.84963 10.5893 4.00591C10.433 4.16219 10.221 4.24999 10 4.24999Z" fill="#43C4F4"/>
												<path d="M13.3333 4.24999C13.1123 4.24999 12.9004 4.16219 12.7441 4.00591C12.5878 3.84963 12.5 3.63767 12.5 3.41666V2.49999C12.5 2.27898 12.5878 2.06701 12.7441 1.91073C12.9004 1.75445 13.1123 1.66666 13.3333 1.66666C13.5543 1.66666 13.7663 1.75445 13.9226 1.91073C14.0789 2.06701 14.1667 2.27898 14.1667 2.49999V3.41666C14.1667 3.63767 14.0789 3.84963 13.9226 4.00591C13.7663 4.16219 13.5543 4.24999 13.3333 4.24999Z" fill="#43C4F4"/>
												<path d="M16.6667 18.3333H3.33333C3.11232 18.3333 2.90036 18.2455 2.74408 18.0892C2.5878 17.933 2.5 17.721 2.5 17.5V4.16665C2.5 3.94563 2.5878 3.73367 2.74408 3.57739C2.90036 3.42111 3.11232 3.33331 3.33333 3.33331H16.6667C16.8877 3.33331 17.0996 3.42111 17.2559 3.57739C17.4122 3.73367 17.5 3.94563 17.5 4.16665V17.5C17.5 17.721 17.4122 17.933 17.2559 18.0892C17.0996 18.2455 16.8877 18.3333 16.6667 18.3333ZM4.16667 16.6666H15.8333V4.99998H4.16667V16.6666Z" fill="#43C4F4"/>
												<path d="M8.33335 14.1667C8.22368 14.1673 8.11496 14.1463 8.01343 14.1048C7.9119 14.0633 7.81955 14.0022 7.74168 13.925L6.07502 12.2583C5.99732 12.1806 5.93568 12.0884 5.89363 11.9869C5.85158 11.8853 5.82994 11.7765 5.82994 11.6667C5.82994 11.4447 5.9181 11.2319 6.07502 11.075C6.23194 10.9181 6.44477 10.8299 6.66668 10.8299C6.8886 10.8299 7.10143 10.9181 7.25835 11.075L8.33335 12.1583L12.7417 7.74166C12.8986 7.58474 13.1114 7.49658 13.3334 7.49658C13.5553 7.49658 13.7681 7.58474 13.925 7.74166C14.0819 7.89858 14.1701 8.11141 14.1701 8.33332C14.1701 8.55524 14.0819 8.76807 13.925 8.92499L8.92502 13.925C8.84715 14.0022 8.7548 14.0633 8.65327 14.1048C8.55174 14.1463 8.44302 14.1673 8.33335 14.1667Z" fill="#43C4F4"/>
												<path d="M6.66666 4.24999C6.44565 4.24999 6.23369 4.16219 6.07741 4.00591C5.92113 3.84963 5.83333 3.63767 5.83333 3.41666V2.49999C5.83333 2.27898 5.92113 2.06701 6.07741 1.91073C6.23369 1.75445 6.44565 1.66666 6.66666 1.66666C6.88768 1.66666 7.09964 1.75445 7.25592 1.91073C7.4122 2.06701 7.5 2.27898 7.5 2.49999V3.41666C7.5 3.63767 7.4122 3.84963 7.25592 4.00591C7.09964 4.16219 6.88768 4.24999 6.66666 4.24999Z" fill="#43C4F4"/>
												<path d="M10 4.24999C9.77899 4.24999 9.56703 4.16219 9.41075 4.00591C9.25447 3.84963 9.16667 3.63767 9.16667 3.41666V2.49999C9.16667 2.27898 9.25447 2.06701 9.41075 1.91073C9.56703 1.75445 9.77899 1.66666 10 1.66666C10.221 1.66666 10.433 1.75445 10.5893 1.91073C10.7455 2.06701 10.8333 2.27898 10.8333 2.49999V3.41666C10.8333 3.63767 10.7455 3.84963 10.5893 4.00591C10.433 4.16219 10.221 4.24999 10 4.24999Z" fill="#43C4F4"/>
												<path d="M13.3333 4.24999C13.1123 4.24999 12.9004 4.16219 12.7441 4.00591C12.5878 3.84963 12.5 3.63767 12.5 3.41666V2.49999C12.5 2.27898 12.5878 2.06701 12.7441 1.91073C12.9004 1.75445 13.1123 1.66666 13.3333 1.66666C13.5543 1.66666 13.7663 1.75445 13.9226 1.91073C14.0789 2.06701 14.1667 2.27898 14.1667 2.49999V3.41666C14.1667 3.63767 14.0789 3.84963 13.9226 4.00591C13.7663 4.16219 13.5543 4.24999 13.3333 4.24999Z" fill="#43C4F4"/>
												<path d="M16.6667 18.3333H3.33333C3.11232 18.3333 2.90036 18.2455 2.74408 18.0892C2.5878 17.933 2.5 17.721 2.5 17.5V4.16665C2.5 3.94563 2.5878 3.73367 2.74408 3.57739C2.90036 3.42111 3.11232 3.33331 3.33333 3.33331H16.6667C16.8877 3.33331 17.0996 3.42111 17.2559 3.57739C17.4122 3.73367 17.5 3.94563 17.5 4.16665V17.5C17.5 17.721 17.4122 17.933 17.2559 18.0892C17.0996 18.2455 16.8877 18.3333 16.6667 18.3333ZM4.16667 16.6666H15.8333V4.99998H4.16667V16.6666Z" fill="#43C4F4"/>
												<path d="M8.33335 14.1667C8.22368 14.1673 8.11496 14.1463 8.01343 14.1048C7.9119 14.0633 7.81955 14.0022 7.74168 13.925L6.07502 12.2583C5.99732 12.1806 5.93568 12.0884 5.89363 11.9869C5.85158 11.8853 5.82994 11.7765 5.82994 11.6667C5.82994 11.4447 5.9181 11.2319 6.07502 11.075C6.23194 10.9181 6.44477 10.8299 6.66668 10.8299C6.8886 10.8299 7.10143 10.9181 7.25835 11.075L8.33335 12.1583L12.7417 7.74166C12.8986 7.58474 13.1114 7.49658 13.3334 7.49658C13.5553 7.49658 13.7681 7.58474 13.925 7.74166C14.0819 7.89858 14.1701 8.11141 14.1701 8.33332C14.1701 8.55524 14.0819 8.76807 13.925 8.92499L8.92502 13.925C8.84715 14.0022 8.7548 14.0633 8.65327 14.1048C8.55174 14.1463 8.44302 14.1673 8.33335 14.1667Z" fill="#43C4F4"/>
											</svg>
											:
											<img class="myPageSl" src="https://f.wishabi.net/creative/Distribution/images/icons/list-outline-default5.svg" alt=""/>
									}
								</div>
								<span>{i18n("shopping_list")}</span>
								{/* <ClippingsPill /> */}
							</Link>
							:
							null
						}
						{this.userAgent.mobile ?
							<Link to="favourites">
								<div class="myCustomElement">
									{
										this.bf.isOnHost() ?
										<img class="myPageFavourites" src="https://f.wishabi.net/creative/Distribution/images/bf/Favourite.png" alt=""/> :
										<img class="myPageFavourites" src="https://f.wishabi.net/creative/Distribution/images/icons/favourite-outline-default5.svg" alt=""/>
									}
								</div>
								<span>{i18n("fav_page")}</span>
							</Link>
							:
							null
						}

						{(!this.userAgent.mobile && !isCrawler()) ?
							<Link to="favourites">
								<div class="myCustomElement">
									{
										this.bf.isOnHost() ?
											<img class="myPageFavourites" src="https://f.wishabi.net/creative/Distribution/images/bf/Favourite.png" alt=""/> :
											<img class="myPageFavourites" src="https://f.wishabi.net/creative/Distribution/images/icons/favourite-outline-default5.svg" alt=""/>
									}
								</div>
								<span>{i18n("fav_page")}</span>
							</Link>
							:
							null
						}
						{!isCrawler() ?
							<Link to="listing">
								<div class="myCustomElement">
									{
										this.bf.isOnHost() ?
										<img class="myPageFlyers" src="https://f.wishabi.net/creative/Distribution/images/bf/Browse.png" alt=""/> :
										<img class="myPageFlyers" src="https://f.wishabi.net/creative/Distribution/images/icons/browse-outline-default5.svg" alt=""/>
									}
								</div>
								<span>{i18n("flyers")}</span>
							</Link>
							:
							null
						}
				{(this.userAgent.mobile && this.config.RUNTIME === RuntimeConfig.DISTRIBUTION) || isCrawler()
							? null
							: <Link id="coupons-link"
									to="coupons">
								<div class="myCustomElement">
									{
										this.bf.isOnHost() ?
											<img class="myPageCoupons" src="https://f.wishabi.net/creative/Distribution/images/bf/Coupon.png" alt=""/> :
											<img class="myPageCoupons" src="https://f.wishabi.net/creative/Distribution/images/icons/coupon-outline-default5.svg" alt=""/>
									}
								</div>
							<span>{i18n("coupons")}</span>
						</Link>}


						{loading ?
							<div flex-grow flex-vertical>
								<hr />
								<div class="categories" flex-grow>
									<div fill-absolute scrollable>
										<h2>{this.show === "coupons" ? i18n("coupons") : i18n("flyers")}</h2>
										<div class="categories" flex-grow flex-vertical>
											<div class="loading">
												<div class="spinner"></div>
											</div>
										</div>
									</div>
								</div>
							</div>
							: this.show === "coupons" ?
								<div flex-grow flex-vertical>
									<hr />
									<div class="categories" flex-grow>
										<div fill-absolute scrollable>
											<h2>{i18n("coupons")}</h2>
											{ categories.map((category) =>
												<Link to={category}>
													<span class={cx({"bf": this.bf.isOnHost()})} flex-grow>{i18n(category.name)}</span>
													<span class="pill">{category.count}</span>
												</Link>
											)}
										</div>
									</div>
								</div>
								:
								<div flex-grow flex-vertical>
									<hr />
									<div class="categories" flex-grow>
										<div fill-absolute scrollable>
											<h2>{i18n("flyers")}</h2>
											{this.renderFlyersCategories(this.userAgent.mobile, categories)}
										</div>
									</div>
								</div>
						}

						{isCrawler() ? null : canada && french ?
							<div ref="languageButton" class="button-area" flex-vertical>
								{ this.config.CDP_SIMPLE_HEADER ?
									<a class="lang"
										href={LinkService.getInstance().linkTo("distribution", {
											publisher_name: this.config.CDP_PUBLISHER_NAME,
											website_url: this.config.CDP_BACK_LINK,
											channel_id: analyticParams["channel_id"],
											locale: "en-ca",
											postal_code: this.location.postalCode,
											traffic_source: "destination_simple"
										})}>
										<span>English</span>
									</a>
									:
									<a class="lang"
										onClick={() => this.switchLang("en-ca")}
									>
										<span>English</span>
									</a>
								}
							</div>
							: canada && !french ?
								<div ref="languageButton" class="button-area" flex-vertical>
									{ this.config.CDP_SIMPLE_HEADER ?
										<a class="lang"
											onClick={this._setLocale.bind(this, "fr-ca")}
											href={LinkService.getInstance().linkTo("distribution", {
												publisher_name: this.config.CDP_PUBLISHER_NAME,
												website_url: this.config.CDP_BACK_LINK,
												channel_id: analyticParams["channel_id"],
												locale: "fr-ca",
												postal_code: this.location.postalCode,
												traffic_source: "destination_simple"
											})}>
											<span>Français</span>
										</a>
										:
										<a class="lang"
											onClick={() => this.switchLang("fr-ca")}
										>
											<span>Français</span>
										</a>
									}
								</div>
								:
								null
						}
					</div>
				</nav>
			</Dialog>
		);
	}

	private getPoweredByText = () => {
		if (this.bannerService.isSmartNews()) {
			return (
				<div class="powered-by">
					Powered by
				</div>
			);
		}

		return null;
	}

	private switchLang = (lang: string) => {
		this._setLocale(lang);
		const categorySlugToName = this.categoryService.getCategoySlugToNameMapping();
		const pathArr = window.location.pathname.split("/").reduce( (res, item) => {
			if (item === "fr-ca" || item === "en-ca") {
				res.push(lang);
			} else if (categorySlugToName[item]) {
				const locale = lang === "fr-ca" ? "fr" :  "en";
				let categoryNameToSet = categorySlugToName[item];
				categoryNameToSet = i18nForLocale(categoryNameToSet, locale);
				categoryNameToSet = slugify(categoryNameToSet);
				res.push(categoryNameToSet);
			} else if (item === "flyers" || item === "circulaires") {
				const flyersNameToSet = lang === "fr-ca" ? "circulaires" : "flyers";
				res.push(flyersNameToSet);
			} else {
				res.push(item);
			}
			return res;
		}, []);

		window.location.href = pathArr.join("/");
	}

	private checkModal = () => {
		const value = this.storage.getItem("redirected_modal_closed");
		if (value) {
			this.isModalClosed = JSON.parse(value).closed;
		}
	}

	private _closeDiscoverSlModal = (e: Event) => {
		if (e) {
			// If clicked on the close X, then prevent the <Link to=sl /> from kicking into gear.
			// ----------------------------------------------------------------------
			e.preventDefault();
		}

		this.isModalClosed = true;
		this.storage.setItem("redirected_modal_closed", JSON.stringify({"closed": true}));
		const el = document.querySelector(".discover-sl-wrapper");
		if (el) {
			el.outerHTML = "";
		}
	}

	private _persistHidingDiscoverSlModal = (e: Event) => {
		this.isModalClosed = true;
		this.storage.setItem("redirected_modal_closed", JSON.stringify({"closed": true}));
		const el = document.querySelector(".discover-sl-wrapper");
		if (el) {
			el.outerHTML = "";
		}
	}

	private openDeleteAccountPopup = () => {
		window.history.pushState({}, "", "data-deletion");
		renderDeleteAccountModal("flyers");
	}

	private renderConfirmDeleteAccountPopup = () => {
		return <div id="delete-account-confirmation" class="delete-account-confirmation-wrapper">
			<div class="delete-account-confirmation" flex-horizontal>
				<div>{icons.user}</div>
				<div class="delete-account-confirmation-message">
					{i18n("delete_account_confirmation")}
				</div>
			</div>
		</div>;
	}

	private _doRender(loading: boolean, categories: Category[], breadcrumbs: IBreadcrumb[]) {
		this.checkModal();
		const modalMessage = i18n(this.storage.getItem("message_key"));
		if (!loading) {
			setTimeout(() => this.checkMobileBanner(), 1000);
		}
		if (!loading && document.querySelector(".discover-sl-wrapper")) {
			setTimeout(() => {
				this.storage.removeItem("message_key");
			}, 0);
		}

		if (!loading && isCrawler()) {
			this.seo.setCrawlerCss();
		}

		const redirectModal = (
			<a class="discover-sl-wrapper" onClick={this._persistHidingDiscoverSlModal}>
				<div class="discover-sl" flex-horizontal>
					<button class="close" onClick={this._closeDiscoverSlModal}>{icons.x}</button>
					<div flex-horizontal>
						<div>{icons.myDiscoverSl}</div>
						<div class="discover-msg">
							<p>{modalMessage}</p>
						</div>
					</div>
				</div>
			</a>);

		const french = this.location.acceptsLocale("fr");
		const circularDestinationContainer = this.config.CDP_SIMPLE_HEADER ? <CircularDestinationHeader /> : null;
		const withSidebar = () => this.withSidebar === "true";
		const isDownloadAppBannerVisible = function () {
			let isCityStorePage = false;
			const path = window.location.pathname;
			const pathArr = path.split("/").filter(t => t !== "");
			if (pathArr.indexOf("stores") > 0 && pathArr[pathArr.length - 1] !== "stores") {
				isCityStorePage = true;
			}
			return !isCrawler() && !isCityStorePage && !this.userAgent.mobile && this.config.RUNTIME !== RuntimeConfig.DISTRIBUTION;
		};

		return <template>
			{/*<DesktopBanner />*/}
			{isDownloadAppBannerVisible.bind(this)() ? <DownloadAppBanner/> : null}
			{isCrawler() ? this.renderBreadcrumbs(breadcrumbs) : null}
			{(isCrawler() && this.merchant) ? this.getMerchantLink() : null}
			{circularDestinationContainer}
			{withSidebar() ? this._renderNavigation(loading, categories) : null}
			<div class="application" flex-grow flex-vertical>
				<MobileBanner checkAppBanner={this.checkAppBanner}/>
				{ (this.config.showAppTopBar && !isCrawler()) ?
					<ApplicationTopBar search={this.search} context={this.context}>
						{this.config.EXPERIENCE === Experience.DEFAULT ?
							<button class="hamburger"
									anchor-point="left top"
									title={i18n("open_navigation_menu")}
									aria-label={i18n("open_navigation_menu")}
									onClick={this._handleHamburgerClick}>
								<div>{icons.hamburger}</div>
								<div class="flipp-logo-wrapper">{icons.flippLogo}</div>
							</button>
							: this.config.EXPERIENCE === Experience.DISTRIBUTION ?
										this.config.aidHero ?
												<div class="home-link" style={{backgroundColor: this.config.getThemeColor()}}>
													<div class="flipp-logo-wrapper">{icons.flippLogo}</div>
												</div>
												:
												<Link class="home-link"
															to="listing"
															onClick={this._handleCrossBrowse}
															style={{backgroundColor: this.config.getThemeColor()}}>
													<div class="flipp-logo-wrapper">{icons.flippLogo}</div>
												</Link>
								:
								null
						}
					</ApplicationTopBar>
					:
					null
				}
				{(!this.isModalClosed && modalMessage) ? redirectModal : null}
				{this.renderConfirmDeleteAccountPopup()}
				<main ref="content" onScroll={this._handleScroll} flex-grow>
					<content />
				</main>
				{
					<Dialog class="welcome" ref="welcome">
						<DialogHeader/>
						<div class={`${this.location.locale === "fr-ca" ? "fr" : ""} content-wrapper`} flex-vertical>

							<img defer src="https://f.wishabi.net/creative/Distribution/images/welcome_to_flipp.png" alt=""/>
							<h2>{i18n("welcome_to_flipp_title")}</h2>

							<p>{i18n("welcome_to_flipp_text")}</p>

							<div class="button-area" flex-horizontal>
								<button class={this.location.locale === "fr-ca" ? "fr" : ""} type="button"
										onClick={this.closeWelcomeDialog.bind(this)}>{i18n("welcome_to_flipp_button")}</button>
							</div>
						</div>
					</Dialog>
				}
				{!isCrawler() ?
					<Dialog class="email-confirm" ref="emailConfirm">
						<div class="content-wrapper" flex-vertical>
							<h2>{i18n("email_signup")}</h2>

							<img defer src="https://f.wishabi.net/creative/Distribution/images/email_notices.png" alt=""/>

							<p>
								{i18n("email_signup_prop")}<a target="_blank" href="https://corp.flipp.com/legal/terms_of_use/">{i18n("email_signup_terms")}</a>
								{i18n("email_signup_prop_2")}<a target="_blank" href="https://corp.flipp.com/legal/privacy/">{i18n("email_signup_privacy")}</a> or
								<a target="_blank" href="https://corp.flipp.com/contact-us/">{i18n("email_signup_contact")}</a>
							</p>
							<div class="button-area" flex-horizontal>
								<button class="decline" type="button"
										onClick={this.close_}>{i18n("email_decline")}</button>
								<button class="accept" type="button"
										onClick={this.acceptCASL_}>{i18n("email_accept")}</button>
							</div>

							<p class="address">Flipp Corporation, 12-3250 Bloor St W, East Tower, Etobicoke, ON, M8X
								2X9, Canada</p>
						</div>
					</Dialog>
					: null
				}

			</div>

			{!isCrawler() ? null :
				<div flex-grow flex-vertical>
					<hr />
					<div class="categories" flex-grow>
						<div fill-absolute scrollable>
							<h2>{i18n("coupons")}</h2>
							{ categories && categories.map((category) =>
								<Link to={category}>
									<span flex-grow>{i18n(category.name)}</span>
									<span class="pill">{category.count}</span>
								</Link>
							)}
						</div>
					</div>
				</div>
			}

			{!isCrawler() ?
				<span>
					<OldBrowserContainer />
					<WebGLContainer />
				</span>
			:
				null
			}
			{this.getFooter()}
		</template>;
	}

	private _handleScroll = (e: Event) => {
		this.dispatchEvent(new Event("scroll"));
	}

	private _handleLocationButtonClick_ = (e: Event) => {
		this.refs.locationDialog.showModal(this.refs.anchor);
	}

	private _handlePostalCodeSubmit = (e: Event) => {
		const input = this.refs.postalCodeInput;
		// clean up
		input.value = input.value.replace(" ", "");

		if (!PostalCode.isValid(input.value)) {
			input.setAttribute("invalid", "");

			e.preventDefault();
			return;
		}

		const currentCode = new PostalCode(this.location.postalCode);
		const newCode = new PostalCode(input.value);
		const hasChangedCountry = currentCode.country !== newCode.country;

		if (this.config.RUNTIME === RuntimeConfig.DISTRIBUTION) {
			/* Prevent users from changing to a different country FSA in the distribution experience */
			if (hasChangedCountry) {
				input.setAttribute("invalid", "");
				e.preventDefault();
				return;
			}
		} else if (hasChangedCountry) {
			const { value } = (e.target as any).locale;
			if (this.location.isUsLocale(value)) {
				(e.target as any).locale.value = "en-ca";
			} else if (this.location.isCanadianLocale(value)) {
				(e.target as any).locale.value = "en-us";
			}
		}

		const postalCode = new PostalCode(input.value);
		this.storage.removeItem("locale");
		this.location.savePostalCode(postalCode);

		// Manually clean publisher name values since form will replace spaces with '+'
		if (this.refs.publisherName) {
			const publisherNameVal = this.refs.publisherName.value;
			this.refs.publisherName.value = publisherNameVal.replace(/\s{1}/g, "__");
		}
	}

	private _handleHamburgerClick = (e: Event) => {
		this.refs.sidebarDialog.showModal(e.currentTarget as Element);
	}

	private _handleResize = (e: Event) => {
		if (!this.userAgent.mobile) {
			if (this.refs.locationDialog.open) this.refs.locationDialog.close();
			if (this.refs.sidebarDialog.open) this.refs.sidebarDialog.close();
		}
	}

	private _setLocale = (lang: string) => {
		if (this.config.DISTRIBUTION_MODE === DistributionMode.SIMPLE || this.config.DISTRIBUTION_MODE === DistributionMode.INLINE) {
			const CDPCookieJSON = Cookies.getJSON("cdp-parameters");
			CDPCookieJSON["locale"] = lang;
			Cookies.set("cdp-parameters", JSON.stringify(CDPCookieJSON), {expires: this.config.CDP_COOKIE_EXPIRY});
		}
		else {
			this.storage.setItem("locale", lang);
		}
	}

	private _handleAuth = (e: CustomEvent) => {
		if (e.detail.action === "login") {
			this.showCasl_();
		}
	}

	private showCasl_ = () => {
		const user = this.auth.user.token;
		if (user.email_subscribed === null) {
			if (this.location.country === Country.CANADA) {
				this.refs.emailConfirm.showModal();
				this.storage.setItem("email_confirmed",
					JSON.stringify({"confirmed": true}));
			} else {
				this.accounts.updateSubscription(user, true);
			}
		}
	}

	private close_ = (evt?: Event) => {
		this.refs.emailConfirm.close();
		this.accounts.updateSubscription(this.auth.user.token, false);
	}

	private acceptCASL_ = (evt: Event) => {
		this.refs.emailConfirm.close();
		this.accounts.updateSubscription(this.auth.user.token, true);
	}

	private isEmailConfirmed = () => {
		const value = this.storage.getItem("email_confirmed");
		if (value) {
			const data = JSON.parse(value);
			return data.confirmed;
		}
		return false;
	}

	private _handleCrossBrowse = () => {
		this.analytics.trackCrossBrowse(CrossBrowseAction.TopLogo);
	}
}

// --- flipp-clippings-pill ------------------------------------>

@element("flipp-clippings-pill")
export class ClippingsPill extends Component<{}, number> {

	@inject clippingService: ClippingService;
	@inject auth: AuthenticationService;
	@inject couponsService: CouponService;

	onAttach() {
		window.addEventListener("flipp-clippingschanged",
				this._handleClippingsChange);
		window.addEventListener("flipp-auth", this._handleAuthChange);
	}

	onDetach() {
		window.removeEventListener("flipp-clippingschanged",
				this._handleClippingsChange);
		window.removeEventListener("flipp-auth", this._handleAuthChange);
	}

	prepare() {
		let clippedCouponsFn = Promise.resolve<ClippedCoupon[]>([]);
		if (this.auth.status === AuthStatus.LOGGED_IN) {
			clippedCouponsFn = this.couponsService.getClippedCoupons(this.auth.user);
		}
		return Promise.all([
			this.couponsService.getCoupons(),
			clippedCouponsFn,
			this.clippingService.getClippings()
		]).then(([coupons, clippedCoupons, clippings]) => {
			/* Create a map of coupon ids for easy lookup */
			const couponIds: {[key: number]: boolean} = {};
			coupons.forEach((coupon) => couponIds[coupon.id] = true);

			const prunedClippings: Clipping[] = [];
			clippings.forEach((clipping) => {
				/* Don't show print coupons that are not available in the current locale */
				if (clipping.type === ClippingType.PRINT_COUPON && couponIds[Number(clipping.item.id)]) {
					prunedClippings.push(clipping);
				} else if (clipping.type !== ClippingType.PRINT_COUPON) {
					prunedClippings.push(clipping);
				}
			});
			return clippedCoupons.length + prunedClippings.length;
		});
	}

	render(count: number) {
		return <span class="pill">{count}</span>;
	}

	private _handleClippingsChange = (e: Event) => {
		this.invalidate();
	}

	private _handleAuthChange = (e: Event) => {
		this.invalidate();
	}

}


export function renderSingleRow({ link, text, locale }: { link: string; text: string, locale: string }) {
	return (<li class="single-link">
		<a href={`/${locale}${link}`}>{text}</a>
	</li>);
}

export function renderFooter(locale: string) {
	const bf = BlackFridayService.getInstance();
	let storesRow1 = storesCanadaRow1;
	let storesRow2 = storesCanadaRow2;
	let storesRow3 = storesCanadaRow3;
	let storeFlyersByCity = storeFlyersCanadaByCity;
	let cityFlyers = locale === "en-ca" ? cityFlyersCanadaEN : cityFlyersCanadaFR;
	let storeCityFlyersLink = `${locale}/store-city-flyers`;
	let countryCityFlyersLink = `${locale}/flyers-canada`;
	let french = "";

	if (locale === "fr-ca") {
		cityFlyers = cityFlyersCanadaFR;
		french = "french";
	}

	if (locale === "en-us") {
		storesRow1 = storesUSARow1;
		storesRow2 = storesUSARow2;
		storesRow3 = storesUSARow3;
		storeFlyersByCity = storeFlyersUSAByCity;
		cityFlyers =  cityFlyersUSA ;
		storeCityFlyersLink = "en-us/store-city-weekly_ads";
		countryCityFlyersLink = "en-us/flyers-usa";
	}

	return (
		<div class={`footer ${french}`}>
			<div class="content-wrapper">
				<div class="subwrapper">
					<div class="leftFloat">
						<ul>
							<li class="uppercase">{i18n("footer_company")}</li>
							<li>
								<a href="https://blog.flipp.com/">{i18n("footer_blog")}</a>
							</li>
							<li>
								<a href="https://corp.flipp.com/partners/">
									<a>{i18n("footer_retailers")}</a>
								</a>
							</li>
							<li>
								<a href="https://corp.flipp.com/job-openings/">
									<a>{i18n("footer_careers")}</a>
								</a>
							</li>
							<li>
								<a href={`/${locale}/stores`}>{i18n("footerStores")}</a>
							</li>
							{bf.isEnabled() ? <li>
								<Link to={"blackFriday"}>
									<span>{i18n("blackfriday_deals")}</span>
								</Link>
							</li> : null}
							<li>
								<a href={i18n("help_link")}>{i18n("helpCenter")}</a>
							</li>
						</ul>
						<ul>
							<li class="uppercase">{i18n("footerStoresFlyers")}</li>
							{storesRow1.map((store) => (
								renderSingleRow({
									link: store.link,
									text: i18n("store_title", { storeName: store.name }),
									locale
								})
							))}
						</ul>
						<ul class="row-without-title">
							{storesRow2.map((store) => (
								renderSingleRow({
									link: store.link,
									text: i18n("store_title", { storeName: store.name }),
									locale
								})
							))}
						</ul>
						<ul class="row-without-title">
							{storesRow3.map((store) => (
								renderSingleRow({
									link: store.link,
									text: i18n("store_title", { storeName: store.name }),
									locale
								})
							))}
						</ul>
						<ul>
							<li class="uppercase">
								<a href={storeCityFlyersLink}>{i18n("footerStoresFlyersByCity")}</a>
							</li>
							{storeFlyersByCity.map((store) => (
								renderSingleRow({
									link: store.link,
									text: i18n("store_locationStore", { city: store.name }),
									locale
								})
							))}
						</ul>
						<ul>
							<li class="uppercase">
								<a href={countryCityFlyersLink}>{i18n("footerCountryCityFlyers")}</a>
							</li>
							{cityFlyers.map((flyer) => (
								renderSingleRow({
									link: flyer.link,
									text: i18n("flyer_location_title", { city: flyer.name }),
									locale
								})
							))}
						</ul>
					</div>
				</div>
			</div>
		</div>
	);
}

const storesCanadaRow1 = [
	{
		name: "Metro",
		link: "/stores/metro",
	},
	{
		name: "Super c",
		link: "/montreal-qc/stores/super-c",
	},
	{
		name: "Food Basics",
		link: "/stores/food-basics",
	},
	{
		name: "Real Canadian Superstore",
		link: "/stores/real-canadian-superstore",
	},
	{
		name: "No Frills",
		link: "/stores/no-frills",
	},
	{
		name: "FreshCo",
		link: "/stores/freshco",
	},
	{
		name: "Foodland",
		link: "/toronto-on/stores/foodland",
	},
	{
		name: "Safeway",
		link: "/vancouver-bc/stores/safeway",
	},
];

const storesUSARow1 = [
	{
		name: "Aldi",
		link: "/stores/aldi",
	},
	{
		name: "Family Dollar",
		link: "/stores/family-dollar",
	},
	{
		name: "Target",
		link: "/stores/target",
	},
	{
		name: "Dollar General",
		link: "/stores/dollar-general",
	},
	{
		name: "Publix",
		link: "/stores/publix",
	},
	{
		name: "Home Depot",
		link: "/stores/home-depot",
	},
];

const storesCanadaRow2 = [
	{
		name: "Zehrs",
		link: "/stores/zehrs?merchant_id=2340 ",
	},
	{
		name: "Costco",
		link: "/stores/costco",
	},
	{
		name: "Loblaws",
		link: "/stores/loblaws",
	},
	{
		name: "Maxi",
		link: "/montreal-qc/stores/maxi",
	},
	{
		name: "Save on Foods",
		link: "/stores/saveonfoods?merchant_id=2062",
	},
	{
		name: "IGA",
		link: "/montreal-qc/stores/iga",
	},
	{
		name: "Co-op",
		link: "/edmonton-ab/stores/co-op?merchant_id=3283",
	},
	{
		name: "Fortinos",
		link: "/mississauga-on/stores/fortinos",
	},
];

const storesUSARow2 = [
	{
		name: "Meijer",
		link: "/stores/meijer",
	},
	{
		name: "Walmart",
		link: "/stores/walmart",
	},
	{
		name: "JCPenney",
		link: "/stores/jcpenney",
	},
	{
		name: "Food Lion",
		link: "/stores/food-lion",
	},
	{
		name: "Albertsons",
		link: "/stores/albertsons",
	},
	{
		name: "CVS Pharmacy",
		link: "/stores/cvs-pharmacy",
	},
	{
		name: "Lowe's",
		link: "/stores/lowes",
	},
	{
		name: "Food 4 Less",
		link: "/stores/food-4-less",
	},
];

const storesCanadaRow3 = [
	{
		name: "Home Depot",
		link: "/stores/home-depot",
	},
	{
		name: "Independent Grocer",
		link: "/stores/your-independent-grocer",
	},
	{
		name: "T&T Supermarket",
		link: "/stores/tandt-supermarket?merchant_id=6373",
	},
	{
		name: "Farm Boy",
		link: "/stores/farm-boy",
	},
	{
		name: "Longo`s",
		link: "/stores/longos",
	},
	{
		name: "Best Buy",
		link: "/stores/best-buy",
	},
	{
		name: "Lowe`s",
		link: "/stores/lowes",
	},
];

const storesUSARow3 = [
	{
		name: "Costco",
		link: "/stores/costco",
	},
	{
		name: "Walgreens",
		link: "/stores/walgreens",
	},
	{
		name: "Dollar Tree",
		link: "/stores/dollar-tree",
	},
	{
		name: "Safeway",
		link: "/stores/safeway",
	},
	{
		name: "Kroger Houston",
		link: "/stores/kroger-houston",
	},
	{
		name: "Big Lots",
		link: "/stores/big-lots",
	},
	{
		name: "Winn-Dixie",
		link: "/stores/winndixie",
	},
	{
		name: "Lidl",
		link: "/stores/lidl",
	},
];

const storeFlyersCanadaByCity = [
	{
		name: "Toronto",
		link: "/toronto-on/stores/",
	},
	{
		name: "Mississauga",
		link: "/mississauga-on/stores/",
	},
	{
		name: "Montreal",
		link: "/montreal-qc/stores/",
	},
	{
		name: "Vancouver",
		link: "/vancouver-bc/stores/",
	},
	{
		name: "Calgary",
		link: "/calgary-ab/stores/",
	},
	{
		name: "Edmonton",
		link: "/edmonton-ab/stores/",
	},
	{
		name: "Ottawa",
		link: "/ottawa-on/stores/",
	},
	{
		name: "Winnipeg",
		link: "/winnipeg-mb/stores/",
	},
	{
		name: "Quebec",
		link: "/quebec-qc/stores/",
	},
	{
		name: "Hamilton",
		link: "/hamilton-on/stores/",
	},
	{
		name: "Kitchener",
		link: "/kitchener-on/stores/",
	},
	{
		name: "London",
		link: "/london-on/stores/",
	},
	{
		name: "Victoria",
		link: "/victoria-bc/stores/",
	},
	{
		name: "Halifax",
		link: "/halifax-ns/stores/",
	},
	{
		name: "Oshawa",
		link: "/oshawa-on/stores/",
	},
	{
		name: "Windsor",
		link: "/windsor-on/stores/",
	},
	{
		name: "Saskatoon",
		link: "/saskatoon-sk/stores/",
	},
];

const storeFlyersUSAByCity = [
	{
		name: "New York",
		link: "/new-york-ny/stores",
	},
	{
		name: "Los Angeles",
		link: "/los-angeles-ca/stores",
	},
	{
		name: "Chicago",
		link: "/chicago-il/stores",
	},
	{
		name: "Houston",
		link: "/houston-tx/stores",
	},
	{
		name: "Phoenix",
		link: "/phoenix-az/stores",
	},
	{
		name: "Philadelphia",
		link: "/philadelphia-pa/stores",
	},
	{
		name: "San Antonio",
		link: "/san-antonio-tx/stores",
	},
	{
		name: "San Diego",
		link: "/san-diego-ca/stores",
	},
	{
		name: "Dallas",
		link: "/dallas-tx/stores",
	},
	{
		name: "Austin",
		link: "/austin-tx/stores",
	},
	{
		name: "Jacksonville",
		link: "/jacksonville-fl/stores",
	},
	{
		name: "San Jose",
		link: "/san-jose-ca/stores",
	},
	{
		name: "Fort Worth",
		link: "/fort-worth-tx/stores",
	},
	{
		name: "Columbus",
		link: "/columbus-oh/stores",
	},
	{
		name: "Charlotte",
		link: "/charlotte-nc/stores",
	},
	{
		name: "Indianapolis",
		link: "/indianapolis-in/stores",
	},
	{
		name: "San Francisco",
		link: "/san-francisco-ca/stores",
	},
	{
		name: "Seattle",
		link: "/seattle-wa/stores",
	},
	{
		name: "Denver",
		link: "/denver-co/stores",
	},
	{
		name: "Oklahoma City",
		link: "/oklahoma-city-ok/stores",
	},
];

const cityFlyersCanadaEN = [
	{
		name: "Calgary",
		link: "/calgary-ab/flyers/",
	},
	{
		name: "Vancouver",
		link: "/vancouver-bc/flyers/",
	},
	{
		name: "Toronto",
		link: "/toronto-on/flyers/",
	},
	{
		name: "Mississauga",
		link: "/mississauga-on/flyers/",
	},
	{
		name: "Montreal",
		link: "/montreal-qc/flyers/",
	},
	{
		name: "Edmonton",
		link: "/edmonton-ab/flyers/",
	},
	{
		name: "Ottawa",
		link: "/ottawa-on/flyers/",
	},
	{
		name: "Winnipeg",
		link: "/winnipeg-mb/flyers/",
	},
	{
		name: "Quebec",
		link: "/quebec-qc/flyers/",
	},
	{
		name: "Hamilton",
		link: "/hamilton-on/flyers/",
	},
	{
		name: "Kitchener",
		link: "/kitchener-on/flyers/",
	},
	{
		name: "London",
		link: "/london-on/flyers/",
	},
	{
		name: "Victoria",
		link: "/victoria-bc/flyers/",
	},
	{
		name: "Halifax",
		link: "/halifax-ns/flyers/",
	},
	{
		name: "Oshawa",
		link: "/oshawa-on/flyers/",
	},
	{
		name: "Windsor",
		link: "/windsor-on/flyers/",
	},
	{
		name: "Saskatoon",
		link: "/saskatoon-sk/flyers/",
	},
];

const cityFlyersCanadaFR = [
	{
		name: "Calgary",
		link: "/calgary-ab/circulaires/",
	},
	{
		name: "Vancouver",
		link: "/vancouver-bc/circulaires/",
	},
	{
		name: "Toronto",
		link: "/toronto-on/circulaires/",
	},
	{
		name: "Mississauga",
		link: "/mississauga-on/circulaires/",
	},
	{
		name: "Montreal",
		link: "/montreal-qc/circulaires/",
	},
	{
		name: "Edmonton",
		link: "/edmonton-ab/circulaires/",
	},
	{
		name: "Ottawa",
		link: "/ottawa-on/circulaires/",
	},
	{
		name: "Winnipeg",
		link: "/winnipeg-mb/circulaires/",
	},
	{
		name: "Quebec",
		link: "/quebec-qc/circulaires/",
	},
	{
		name: "Hamilton",
		link: "/hamilton-on/circulaires/",
	},
	{
		name: "Kitchener",
		link: "/kitchener-on/circulaires/",
	},
	{
		name: "London",
		link: "/london-on/circulaires/",
	},
	{
		name: "Victoria",
		link: "/victoria-bc/circulaires/",
	},
	{
		name: "Halifax",
		link: "/halifax-ns/circulaires/",
	},
	{
		name: "Oshawa",
		link: "/oshawa-on/circulaires/",
	},
	{
		name: "Windsor",
		link: "/windsor-on/circulaires/",
	},
	{
		name: "Saskatoon",
		link: "/saskatoon-sk/circulaires/",
	},
];

const cityFlyersUSA = [
	{
		name: "New York",
		link: "/new-york-ny/weekly_ads",
	},
	{
		name: "Los Angeles",
		link: "/los-angeles-ca/weekly_ads",
	},
	{
		name: "Chicago",
		link: "/chicago-il/weekly_ads",
	},
	{
		name: "Houston",
		link: "/houston-tx/weekly_ads",
	},
	{
		name: "Phoenix",
		link: "/phoenix-az/weekly_ads",
	},
	{
		name: "Philadelphia",
		link: "/philadelphia-pa/weekly_ads",
	},
	{
		name: "San Antonio",
		link: "/san-antonio-tx/weekly_ads",
	},
	{
		name: "San Diego",
		link: "/san-diego-ca/weekly_ads",
	},
	{
		name: "Dallas",
		link: "/dallas-tx/weekly_ads",
	},
	{
		name: "Austin",
		link: "/austin-tx/weekly_ads",
	},
	{
		name: "Jacksonville",
		link: "/jacksonville-fl/weekly_ads",
	},
	{
		name: "San Jose",
		link: "/san-jose-ca/weekly_ads",
	},
	{
		name: "Fort Worth",
		link: "/fort-worth-tx/weekly_ads",
	},
	{
		name: "Columbus",
		link: "/columbus-oh/weekly_ads",
	},
	{
		name: "Charlotte",
		link: "/charlotte-nc/weekly_ads",
	},
	{
		name: "Indianapolis",
		link: "/indianapolis-in/weekly_ads",
	},
	{
		name: "San Francisco",
		link: "/san-francisco-ca/weekly_ads",
	},
	{
		name: "Seattle",
		link: "/seattle-wa/weekly_ads",
	},
	{
		name: "Denver",
		link: "/denver-co/weekly_ads",
	},
	{
		name: "Oklahoma City",
		link: "/oklahoma-city-ok/weekly_ads",
	},
];
